'use client';
import React, { useRef, useState, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button, ButtonClose } from '@ui';

export type PopoverPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end';

interface PopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  placement?: PopoverPlacement;
  header?: React.ReactNode;
  popoverType?: string;
  showCloseButton?: boolean;
  className?: string; // for popover panel
  triggerClassName?: string; // for trigger button
}

export const Popover: React.FC<PopoverProps> = ({
  trigger,
  children,
  placement = 'bottom',
  header,
  popoverType,
  showCloseButton = true,
  className = '',
  triggerClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Dismiss on outside click
  React.useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const updatePosition = React.useCallback(() => {
    if (!open || !triggerRef.current || !popoverRef.current) {
      setCoords(null);
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const gap = 8;

    let top = 0;
    let left = 0;
    const anchorX = triggerRect.left + triggerRect.width / 2;
    const anchorY = triggerRect.top + triggerRect.height / 2;

    switch (placement) {
      case 'top':
        top = triggerRect.top - popoverRect.height - gap;
        left = anchorX - popoverRect.width / 2;
        break;
      case 'bottom':
        top = triggerRect.bottom + gap;
        left = anchorX - popoverRect.width / 2;
        break;
      case 'left':
        top = anchorY - popoverRect.height / 2;
        left = triggerRect.left - popoverRect.width - gap;
        break;
      case 'right':
        top = anchorY - popoverRect.height / 2;
        left = triggerRect.right + gap;
        break;
      case 'top-start':
        top = triggerRect.top - popoverRect.height - gap;
        left = triggerRect.left;
        break;
      case 'top-end':
        top = triggerRect.top - popoverRect.height - gap;
        left = triggerRect.right - popoverRect.width;
        break;
      case 'bottom-start':
        top = triggerRect.bottom + gap;
        left = triggerRect.left;
        break;
      case 'bottom-end':
        top = triggerRect.bottom + gap;
        left = triggerRect.right - popoverRect.width;
        break;
    }

    const padding = 8;
    const maxLeft = window.innerWidth - popoverRect.width - padding;
    const maxTop = window.innerHeight - popoverRect.height - padding;

    const clampedLeft = Math.min(Math.max(left, padding), Math.max(maxLeft, padding));
    const clampedTop = Math.min(Math.max(top, padding), Math.max(maxTop, padding));

    setCoords({ top: clampedTop, left: clampedLeft });
  }, [open, placement]);

  // Calculate popover position when open and keep it anchored on scroll/resize
  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  React.useEffect(() => {
    if (!open) return;
    const handleUpdate = () => updatePosition();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);
    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [open, updatePosition]);

  // Render popover in portal
  const popoverNode = open
    ? ReactDOM.createPortal(
        <div
          ref={popoverRef}
          className={['popover', `popover-${placement}`, popoverType, className]
            .filter(Boolean)
            .join(' ')}
          role='dialog'
          aria-modal='false'
          tabIndex={-1}
          style={{
            position: 'fixed',
            zIndex: 1200,
            minWidth: 200,
            top: coords ? coords.top : 0,
            left: coords ? coords.left : 0,
            visibility: coords ? 'visible' : 'hidden',
          }}
        >
          {header && <div className='popover-header'>{header}</div>}
          <div className='popover-body'>{children}</div>
          {showCloseButton && (
            <ButtonClose
              className='popover-close'
              aria-label='Close'
              onClick={() => setOpen(false)}
            />
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <Button
        ref={triggerRef}
        className={`popover-trigger${triggerClassName ? ' ' + triggerClassName : ''}`}
        aria-haspopup='dialog'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        as='button'
        type='button'
        label={typeof trigger === 'string' ? trigger : ''}
      >
        {typeof trigger === 'string' ? null : trigger}
      </Button>
      {popoverNode}
    </span>
  );
};
